import {
  TicketsContainer,
  EmptyStateLayout,
  EmptyStateContainer,
  NoTicketsIcon,
  NoFiltersMatchIcon,
  LayoutWrapper,
  HeaderWrapper,
  FooterWrapper,
  ButtonNewTicket,
} from './ServiceDeskMobile.styled';
import { TabHeader } from './TabHeader/TabHeader';
import { SingleTicket } from './SingleTicket/SingleTicket';
import { formatDate } from 'src/utils/dateFns/formatDate';
import { SortModal } from './SortModal/SortModal';
import { FilterModal } from './FilterModal/FilterModal';
import { useServiceDeskContext } from 'src/context/ServiceDesk/ServiceDeskContext';
import { LoadingSpinner } from 'src/components/common/LoadingSpinner/LoadingSpinner';
import { EmptyTableBody } from '../../common/EmptyTableBody/EmptyTableBody';
import { TicketsAddPopup } from '../../common/TicketsAddPopup/TicketsAddPopup';
import { useGetTicketTypes } from '../../hooks/useGetTicketTypes';
import { useGetTicketApprovalStatuses } from '../../hooks/useGetTicketApprovalStatuses';
import { useGetTicketPriorities } from '../../hooks/useGetTicketPriorities';
import { useGetTicketCategories } from '../../hooks/useGetTicketCategories';
import { useCreateTicket } from '../../hooks/useCreateTicket';
import { useContext, useEffect, useRef } from 'react';
import { OptionSet } from 'src/components/Tickets/models/Tickets.interface';
import { TeamsFxContext } from 'src/context/TeamsContext';
import { Alert } from 'src/components/common/AlertMessage/AlertMessage';
import { TicketDetailsMobile } from './TicketDetails/TicketDetailsMobile';
import { Add20Regular } from '@fluentui/react-icons';
import { Spinner } from '@fluentui/react-components';

export const MyTicketsMobile = () => {
  const { themeString } = useContext(TeamsFxContext);
  const scrollElementRef = useRef<HTMLDivElement>(null);

  const {
    ticketsDataTableAll,
    ticketsDataModified,
    ticketDataModifiedInfinite,
    currentPageNumber,
    totalPagesNumber,
    isReached,
    setIsReached,
    handleNextPageNumber,
    isLoadingInfinite,
    setIsLoadingInfinite,
    isMobileFilterOpen,
    isMobileSortOpen,
    loadingTickets,
    emptyState,
    addTicketPopup,
    handleSubmittedTicket,
    isSelectedFilter,
    toggleAddTicketPopup,
    viewTicketDetailsPopup,
  } = useServiceDeskContext();
  const { ticketTypes, isLoadingTicketTypes } = useGetTicketTypes();
  const { ticketApprovalStatuses, isLoadingTicketApprovalStatuses } =
    useGetTicketApprovalStatuses();
  const { ticketPriorities, isLoadingTicketPriorities } =
    useGetTicketPriorities();
  const { ticketCategories, isLoadingTicketCategories } =
    useGetTicketCategories();
  const {
    createTicket,
    isLoadingCreateTicket,
    hideAlert,
    requestStatusAppearance,
    requestStatusMessage,
  } = useCreateTicket();

  const parsedTicketCategories: OptionSet[] = ticketCategories?.map(
    (category) => {
      return {
        value: category.new_axsticketcategoryid,
        label: category.new_name,
      };
    }
  );

  const loadMore = () => {
    if (currentPageNumber >= totalPagesNumber) {
      return;
    }
    setIsLoadingInfinite(true);
    handleNextPageNumber();
  };
  const handleScroll = () => {
    const el = scrollElementRef?.current;
    if (el) {
      const scrollTop = el.scrollTop;
      const scrollHeight = el.scrollHeight;
      const clientHeight = el.clientHeight;
      const reachPoint = scrollHeight - clientHeight - 50;
      if (scrollTop >= reachPoint && !isReached[currentPageNumber]) {
        setIsReached((prev) => ({ ...prev, [currentPageNumber]: true }));
      }
    }
  };

  useEffect(() => {
    if (scrollElementRef?.current && !isReached[currentPageNumber]) {
      scrollElementRef?.current.addEventListener('scroll', handleScroll);

      return () => {
        scrollElementRef?.current?.removeEventListener('scroll', handleScroll);
      };
    }
  }, [scrollElementRef?.current, ticketDataModifiedInfinite]);

  useEffect(() => {
    if (isReached[currentPageNumber]) {
      loadMore();
    }
  }, [isReached[currentPageNumber]]);

  if (
    loadingTickets ||
    isLoadingTicketTypes ||
    isLoadingTicketApprovalStatuses ||
    isLoadingTicketPriorities ||
    isLoadingTicketCategories
  ) {
    return (
      <>
        <TabHeader />
        <LoadingSpinner />
        <TicketsContainer></TicketsContainer>
      </>
    );
  }

  return (
    <>
      {isMobileSortOpen && <SortModal />}
      {isMobileFilterOpen && <FilterModal />}
      {viewTicketDetailsPopup && <TicketDetailsMobile />}

      {requestStatusMessage && (
        <Alert
          requestStatusAppearance={requestStatusAppearance}
          requestStatusMessage={requestStatusMessage}
          showCloseButton={true}
          overlay={true}
          handleClose={hideAlert}
        />
      )}
      {addTicketPopup && (
        <TicketsAddPopup
          ticketTypes={ticketTypes}
          ticketApprovalStatuses={ticketApprovalStatuses}
          ticketCategories={parsedTicketCategories}
          ticketPriorities={ticketPriorities}
          createTicket={createTicket}
          isLoadingCreateTicket={isLoadingCreateTicket}
          handleSubmittedTicket={handleSubmittedTicket}
        ></TicketsAddPopup>
      )}
      {!viewTicketDetailsPopup &&
        !addTicketPopup &&
        ticketsDataTableAll &&
        ticketsDataModified?.length > 0 && (
          <LayoutWrapper>
            <HeaderWrapper>
              <TabHeader />
            </HeaderWrapper>
            <TicketsContainer ref={scrollElementRef}>
              {Object.values(ticketDataModifiedInfinite)?.map((data) => {
                return data?.map(
                  ({
                    new_id,
                    createdon,
                    new_description,
                    statuscode,
                    new_axsticketid,
                    owner,
                  }) => {
                    const { month, day, year } = formatDate(
                      new Date(createdon)
                    );
                    const createdOnFormatted = `${month}/${day}/${year}`;
                    return (
                      <SingleTicket
                        key={new_id}
                        id={new_id}
                        createdOn={createdOnFormatted}
                        description={new_description}
                        status={statuscode}
                        axsTicketID={new_axsticketid}
                        owner={owner}
                      />
                    );
                  }
                );
              })}

              {isLoadingInfinite && (
                <Spinner
                  appearance={themeString === 'dark' ? 'inverted' : 'primary'}
                />
              )}
              <FooterWrapper>
                <ButtonNewTicket
                  className="footerOutOfViewPort"
                  onClick={() => toggleAddTicketPopup(true)}
                  size="medium"
                  icon={<Add20Regular />}
                  iconPosition="before"
                >
                  New Ticket
                </ButtonNewTicket>
              </FooterWrapper>
            </TicketsContainer>
          </LayoutWrapper>
        )}
      {!ticketsDataModified && (
        <EmptyStateLayout>
          <EmptyStateContainer>
            <EmptyTableBody
              title={emptyState.noTickets.title}
              subtitle={emptyState.noTickets.subtitle}
              icon={<NoTicketsIcon />}
              showAddTicketButton={true}
              emptyTableBodyButtonLabel="Submit a new ticket"
              toggleAddTicketPopup={toggleAddTicketPopup}
            />
          </EmptyStateContainer>
        </EmptyStateLayout>
      )}
      {ticketsDataModified &&
        ticketsDataModified?.length === 0 &&
        (isSelectedFilter ? (
          <>
            <TabHeader />
            <EmptyStateLayout>
              <EmptyStateContainer>
                <EmptyTableBody
                  title={emptyState.noFilters.title}
                  subtitle={emptyState.noFilters.subtitle}
                  icon={<NoFiltersMatchIcon />}
                />
              </EmptyStateContainer>
            </EmptyStateLayout>
          </>
        ) : (
          <EmptyStateLayout>
            <EmptyStateContainer>
              <EmptyTableBody
                title={emptyState.noTickets.title}
                subtitle={emptyState.noTickets.subtitle}
                icon={<NoTicketsIcon />}
                showAddTicketButton={true}
                emptyTableBodyButtonLabel="Submit a new ticket"
                toggleAddTicketPopup={toggleAddTicketPopup}
              />
            </EmptyStateContainer>
          </EmptyStateLayout>
        ))}
    </>
  );
};
